import { Component, DOCUMENT, afterNextRender, effect, inject, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { NgOptimizedImage } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { MegamenuSolucionesComponent } from '../megamenu-soluciones/megamenu-soluciones';
import { MegamenuCamionesComponent } from '../megamenu-camiones/megamenu-camiones';
import { IndustriesService } from '../../services/industries.service';
import { TrucksService } from '../../services/trucks.service';
import { RegSupPipe } from '../../pipes/reg-sup.pipe';

export interface NavItem {
  label: string;
  url: string;
  external?: boolean;
  noNav?: boolean;
  megamenu?: 'soluciones' | 'camiones';
  mobileOnly?: boolean;
  children?: Omit<NavItem, 'children'>[];
}

@Component({
  selector: 'app-menu-principal',
  standalone: true,
  imports: [
    RouterLink,
    RouterLinkActive,
    NgOptimizedImage,
    MegamenuSolucionesComponent,
    MegamenuCamionesComponent,
    RegSupPipe,
  ],
  templateUrl: './menu-principal.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './menu-principal.scss',
})
export class MenuPrincipalComponent {
  @ViewChild(MegamenuCamionesComponent) private megamenuCamiones!: MegamenuCamionesComponent;
  @ViewChild(MegamenuSolucionesComponent) megamenuSoluciones?: MegamenuSolucionesComponent;

  private industriesService = inject(IndustriesService);
  private trucksService = inject(TrucksService);
  readonly industriesMobile = toSignal(this.industriesService.getIndustries(), { initialValue: [] });
  readonly trucksMobile = toSignal(
    this.trucksService.getTrucks().pipe(map((trucks) => trucks.filter((t) => t.showMegaMenu))),
    { initialValue: [] },
  );

  readonly leftNav: NavItem[] = [
    {
      label: 'Camiones',
      url: '/camiones',
      megamenu: 'camiones',
    },
    {
      label: 'Industrias',
      url: '/industrias',
      mobileOnly: true,
    },
    {
      label: 'Soluciones',
      url: '/servicio',
      noNav: true,
      megamenu: 'soluciones',
      children: [
        { label: 'Mack con todo', url: '/programas-integrales/mack-con-todo' },
        { label: 'Mack total', url: '/programas-integrales/mack-total' },
        { label: 'Financiamiento', url: '/mack-financial-services' },
      ],
    },
    {
      label: 'Refacciones',
      url: 'https://tienda.camionesmack.mx/',
      external: true,
      children: [
        { label: 'Mantenimiento', url: '/refacciones/mantenimiento' },
        { label: 'Conectividad', url: '/refacciones/conectividad' },
      ],
    },
  ];

  readonly rightNav: NavItem[] = [
    { label: 'Blog', url: '/blog' },
    { label: 'Contacto', url: '/contacto' },
  ];

  readonly cta: NavItem = { label: 'Contacta a un asesor', url: '/contacto' };

  mobileOpen = signal(false);
  openSubmenu = signal<string | null>(null);
  activePanel = signal<NavItem | null>(null);
  megamenuOpen = signal<NavItem['megamenu'] | null>(null);
  activeSolucionesRail = signal<{ key: string; label: string } | null>(null);
  hidden = signal(false);

  private readonly document = inject(DOCUMENT);
  private closeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    effect(() => {
      if (this.megamenuOpen() !== 'camiones') {
        this.megamenuCamiones?.resetScroll();
      }
    });
    effect(() => {
      this.document.body.classList.toggle('no-scroll', this.mobileOpen());
    });
    effect(() => {
      const root = this.document.documentElement;
      if (this.hidden()) {
        root.style.setProperty('--header-top', '0px');
      } else {
        root.style.removeProperty('--header-top');
      }
    });
    afterNextRender(() => {
      const win = this.document.defaultView;
      if (!win) return;
      let lastY = win.scrollY;
      win.addEventListener(
        'scroll',
        () => {
          const y = win.scrollY;
          if (win.innerWidth <= 992) {
            this.hidden.set(false);
          } else if (y > 800 && y > lastY) {
            this.hidden.set(true);
          } else if (y < lastY || y < 800) {
            this.hidden.set(false);
          }
          lastY = y;
        },
        { passive: true },
      );
    });
  }

  toggleMobile() {
    this.mobileOpen.update((v) => !v);
    if (!this.mobileOpen()) {
      this.openSubmenu.set(null);
      this.activePanel.set(null);
      this.activeSolucionesRail.set(null);
    }
  }

  closeMobile() {
    setTimeout(() => {
      this.mobileOpen.set(false);
      this.openSubmenu.set(null);
      this.activePanel.set(null);
      this.activeSolucionesRail.set(null);
    }, 0);
  }

  toggleSubmenu(url: string) {
    this.openSubmenu.update((v) => (v === url ? null : url));
  }

  openPanel(item: NavItem) {
    this.activePanel.set(item);
    this.activeSolucionesRail.set(null);
  }

  closePanel() {
    this.activePanel.set(null);
    this.activeSolucionesRail.set(null);
  }

  openSolucionesRail(rail: { key: string; label: string }) {
    this.activeSolucionesRail.set(rail);
  }

  closeSolucionesRail() {
    this.activeSolucionesRail.set(null);
  }

  onItemEnter(item: NavItem) {
    this.cancelClose();
    this.megamenuOpen.set(item.megamenu ?? null);
  }

  onItemLeave() {
    this.scheduleClose();
  }

  cancelClose() {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  scheduleClose() {
    this.cancelClose();
    this.closeTimer = setTimeout(() => this.megamenuOpen.set(null), 150);
  }
}
